import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';

// Replace with your MapBox access token
// Get a new token from: https://account.mapbox.com/
// Required permissions: styles:read, styles:tiles
// IMPORTANT: Use a PUBLIC token (pk.eyJ1...) for client-side apps, not a SECRET token (sk.eyJ1...)
mapboxgl.accessToken = 'pk.eyJ1IjoiZGF2ZW1raWkiLCJhIjoiY21kb2M2cjl4MDBkNzJxcjJrZHJpdGgydCJ9.8VrPgCWGGr5YUR8O9CDEzQ';

// Color palette for different shipments
const shipmentColors = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // yellow
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

const MapPanel = ({ shipment, shipments, anomalies, allAnomalies, overviewMode }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);

  // Safe function to remove map
  const removeMap = () => {
    if (map.current && map.current._removed === false) {
      try {
        map.current.remove();
      } catch (error) {
        console.warn('MapPanel: Error removing map:', error);
      }
      map.current = null;
    }
  };

  useEffect(() => {
    console.log('MapPanel: Component mounted');
    console.log('MapPanel: Overview mode:', overviewMode);
    console.log('MapPanel: Shipment:', shipment?.id);
    console.log('MapPanel: Shipments count:', shipments?.length);
    
    // Wait a bit for the DOM to be ready
    const timer = setTimeout(() => {
      console.log('MapPanel: Initializing map');
      console.log('MapPanel: MapBox token:', mapboxgl.accessToken ? 'Present' : 'Missing');
      console.log('MapPanel: Token length:', mapboxgl.accessToken?.length);
      
      // Use ref instead of getElementById
      const container = mapContainer.current;
      console.log('MapPanel: Container element:', container);
      
      // Safely remove existing map
      removeMap();

      // Check if we have a valid MapBox token
      if (!mapboxgl.accessToken || mapboxgl.accessToken.length < 10) {
        console.error('MapPanel: Invalid MapBox token');
        setMapError(true);
        setMapLoading(false);
        return;
      }

      // Function to initialize map
      const initMap = () => {
        console.log('MapPanel: Container element:', container);
        
        // Check if container exists
        if (!container) {
          console.error('MapPanel: Container element not found');
          return false;
        }

        try {
          console.log('MapPanel: Creating new map instance');
          
          // Create a simple map first to test the token
          map.current = new mapboxgl.Map({
            container: container,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [-0.1276, 51.5074], // London coordinates
            zoom: 5
          });

          map.current.on('load', () => {
            console.log('MapPanel: Map loaded successfully');
            console.log('MapPanel: Overview mode:', overviewMode);
            console.log('MapPanel: Shipment:', shipment?.id);
            console.log('MapPanel: Shipments count:', shipments?.length);
            setMapLoading(false);
            
            // Add shipment data to the map
            if (overviewMode && shipments && shipments.length > 0) {
              console.log('MapPanel: Adding all shipments to map');
              addAllShipmentsToMap();
            } else if (!overviewMode && shipment && shipment.route) {
              console.log('MapPanel: Adding single shipment to map');
              addShipmentToMap();
            } else {
              console.log('MapPanel: No data to add to map');
              console.log('MapPanel: Overview mode:', overviewMode);
              console.log('MapPanel: Has shipment:', !!shipment);
              console.log('MapPanel: Has shipments:', !!shipments);
              console.log('MapPanel: Shipments count:', shipments?.length);
            }
          });

          map.current.on('error', (e) => {
            console.error('MapPanel: MapBox error:', e);
            setMapError(true);
            setMapLoading(false);
          });

          return true;
        } catch (error) {
          console.error('MapPanel: MapBox initialization error:', error);
          setMapError(true);
          setMapLoading(false);
          return false;
        }
      };

      // Function to add single shipment data to the map
      const addShipmentToMap = () => {
        if (!map.current || !shipment) return;

        console.log('MapPanel: Adding single shipment data to map:', shipment.id);
        console.log('MapPanel: Route coordinates:', shipment.route);
        console.log('MapPanel: Anomalies:', anomalies);

        // Remove existing layers and sources if they exist
        try {
          // Remove all route layers and sources
          const layersToRemove = ['route-line'];
          const sourcesToRemove = ['route'];
          
          // Also remove any overview route layers
          for (let i = 0; i < 10; i++) {
            layersToRemove.push(`route-line-shipment-${i}`);
            sourcesToRemove.push(`route-shipment-${i}`);
          }
          
          layersToRemove.forEach(layerId => {
            if (map.current.getLayer(layerId)) {
              map.current.removeLayer(layerId);
            }
          });
          
          sourcesToRemove.forEach(sourceId => {
            if (map.current.getSource(sourceId)) {
              map.current.removeSource(sourceId);
            }
          });
        } catch (error) {
          console.warn('MapPanel: Error removing existing layers:', error);
        }

        // Add route line
        if (shipment.route && shipment.route.length > 1) {
          // Validate route coordinates
          const validRoute = shipment.route.filter(coord => 
            Array.isArray(coord) && 
            coord.length === 2 && 
            typeof coord[0] === 'number' && 
            typeof coord[1] === 'number' &&
            coord[0] >= -180 && coord[0] <= 180 &&
            coord[1] >= -90 && coord[1] <= 90
          );
          
          if (validRoute.length > 1) {
            console.log('MapPanel: Adding valid route with', validRoute.length, 'coordinates');
            console.log('MapPanel: Route start:', validRoute[0]);
            console.log('MapPanel: Route end:', validRoute[validRoute.length - 1]);
            
            try {
              map.current.addSource('route', {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: validRoute
                  }
                }
              });

              map.current.addLayer({
                id: 'route-line',
                type: 'line',
                source: 'route',
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round'
                },
                paint: {
                  'line-color': '#3b82f6',
                  'line-width': 4,
                  'line-opacity': 0.8
                }
              });
            } catch (error) {
              console.warn('MapPanel: Error adding route:', error);
            }
          } else {
            console.warn('MapPanel: Invalid route data, skipping route display');
          }
        } else {
          console.warn('MapPanel: No valid route data found');
        }

        // Add origin marker
        if (shipment.route && shipment.route.length > 0) {
          try {
            const origin = shipment.route[0];
            const originEl = document.createElement('div');
            originEl.className = 'marker origin-marker';
            originEl.style.width = '20px';
            originEl.style.height = '20px';
            originEl.style.borderRadius = '50%';
            originEl.style.backgroundColor = '#10b981';
            originEl.style.border = '3px solid white';
            originEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
            originEl.style.cursor = 'pointer';

            new mapboxgl.Marker(originEl)
              .setLngLat(origin)
              .setPopup(new mapboxgl.Popup().setHTML(`<h3>Origin</h3><p>${shipment.origin}</p>`))
              .addTo(map.current);
          } catch (error) {
            console.warn('MapPanel: Error adding origin marker:', error);
          }
        }

        // Add destination marker
        if (shipment.route && shipment.route.length > 1) {
          try {
            const destination = shipment.route[shipment.route.length - 1];
            const destEl = document.createElement('div');
            destEl.className = 'marker destination-marker';
            destEl.style.width = '20px';
            destEl.style.height = '20px';
            destEl.style.borderRadius = '50%';
            destEl.style.backgroundColor = '#ef4444';
            destEl.style.border = '3px solid white';
            destEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
            destEl.style.cursor = 'pointer';

            new mapboxgl.Marker(destEl)
              .setLngLat(destination)
              .setPopup(new mapboxgl.Popup().setHTML(`<h3>Destination</h3><p>${shipment.destination}</p>`))
              .addTo(map.current);
          } catch (error) {
            console.warn('MapPanel: Error adding destination marker:', error);
          }
        }

        // Add anomaly markers (only first instance of each type)
        if (anomalies && anomalies.length > 0) {
          // Group anomalies by type and severity, take only the first instance
          const uniqueAnomalies = [];
          const seenAnomalyTypes = new Set();
          
          anomalies.forEach((anomaly) => {
            const anomalyKey = `${anomaly.type}_${anomaly.severity}`;
            if (!seenAnomalyTypes.has(anomalyKey) && anomaly.location) {
              uniqueAnomalies.push(anomaly);
              seenAnomalyTypes.add(anomalyKey);
            }
          });
          
          console.log(`MapPanel: Showing ${uniqueAnomalies.length} unique anomalies out of ${anomalies.length} total`);
          
          uniqueAnomalies.forEach((anomaly) => {
            try {
              const anomalyEl = document.createElement('div');
              anomalyEl.className = 'marker anomaly-marker';
              anomalyEl.style.width = '16px';
              anomalyEl.style.height = '16px';
              anomalyEl.style.borderRadius = '50%';
              anomalyEl.style.backgroundColor = anomaly.severity === 'critical' ? '#ef4444' : '#f59e0b';
              anomalyEl.style.border = '2px solid white';
              anomalyEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
              anomalyEl.style.cursor = 'pointer';

              const popupContent = `
                <div style="min-width: 200px;">
                  <h3 style="margin: 0 0 8px 0; color: ${anomaly.severity === 'critical' ? '#ef4444' : '#f59e0b'};">
                    ${anomaly.type.replace('_', ' ').toUpperCase()}
                  </h3>
                  <p style="margin: 0 0 8px 0; font-size: 14px;">${anomaly.description}</p>
                  <p style="margin: 0; font-size: 12px; color: #666;">
                    ${new Date(anomaly.timestamp).toLocaleString()}
                  </p>
                  <div style="margin-top: 8px; font-size: 12px;">
                    <strong>Sensor Data:</strong><br/>
                    Temp: ${anomaly.sensorData.temperature}°C<br/>
                    Humidity: ${anomaly.sensorData.humidity}%<br/>
                    Light: ${anomaly.sensorData.light} lux<br/>
                    Motion: ${anomaly.sensorData.motion}<br/>
                    Gas: ${anomaly.sensorData.gas}
                  </div>
                </div>
              `;

              new mapboxgl.Marker(anomalyEl)
                .setLngLat(anomaly.location)
                .setPopup(new mapboxgl.Popup().setHTML(popupContent))
                .addTo(map.current);
            } catch (error) {
              console.warn('MapPanel: Error adding anomaly marker:', error);
            }
          });
        }

        // Fit map to show all markers
        if (shipment.route && shipment.route.length > 0) {
          try {
            const bounds = new mapboxgl.LngLatBounds();
            shipment.route.forEach(coord => bounds.extend(coord));
            anomalies.forEach(anomaly => {
              if (anomaly.location) {
                bounds.extend(anomaly.location);
              }
            });
            map.current.fitBounds(bounds, { padding: 50 });
          } catch (error) {
            console.warn('MapPanel: Error fitting bounds:', error);
          }
        }

        console.log('MapPanel: Single shipment data added to map successfully');
      };

      // Function to add all shipments to the map
      const addAllShipmentsToMap = () => {
        if (!map.current || !shipments) return;

        console.log('MapPanel: Adding all shipments to map:', shipments.length);
        
        const bounds = new mapboxgl.LngLatBounds();
        const allAnomalyList = [];

        // Collect all anomalies
        if (allAnomalies) {
          Object.values(allAnomalies).forEach(anomalyList => {
            allAnomalyList.push(...anomalyList);
          });
        }

        shipments.forEach((shipment, index) => {
          const color = shipmentColors[index % shipmentColors.length];
          
          console.log(`MapPanel: Adding shipment ${shipment.id} with color ${color}`);

          // Add route line for this shipment
          if (shipment.route && shipment.route.length > 1) {
            try {
              const sourceId = `route-${shipment.id}`;
              const layerId = `route-line-${shipment.id}`;

              map.current.addSource(sourceId, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: shipment.route
                  }
                }
              });

              map.current.addLayer({
                id: layerId,
                type: 'line',
                source: sourceId,
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round'
                },
                paint: {
                  'line-color': color,
                  'line-width': 3,
                  'line-opacity': 0.8
                }
              });
            } catch (error) {
              console.warn(`MapPanel: Error adding route for ${shipment.id}:`, error);
            }
          }

          // Add origin marker
          if (shipment.route && shipment.route.length > 0) {
            try {
              const origin = shipment.route[0];
              const originEl = document.createElement('div');
              originEl.className = 'marker origin-marker';
              originEl.style.width = '16px';
              originEl.style.height = '16px';
              originEl.style.borderRadius = '50%';
              originEl.style.backgroundColor = color;
              originEl.style.border = '2px solid white';
              originEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
              originEl.style.cursor = 'pointer';

              const popupContent = `
                <div style="min-width: 200px;">
                  <h3 style="margin: 0 0 8px 0; color: ${color};">${shipment.id}</h3>
                  <p style="margin: 0 0 8px 0; font-size: 14px;"><strong>Origin:</strong> ${shipment.origin}</p>
                  <p style="margin: 0 0 8px 0; font-size: 14px;"><strong>Destination:</strong> ${shipment.destination}</p>
                  <p style="margin: 0 0 8px 0; font-size: 12px;"><strong>Status:</strong> ${shipment.status}</p>
                  <p style="margin: 0 0 8px 0; font-size: 12px;"><strong>Distance:</strong> ${shipment.distance} km</p>
                  <p style="margin: 0 0 8px 0; font-size: 12px;"><strong>Duration:</strong> ${shipment.duration} hrs</p>
                  <p style="margin: 0; font-size: 12px;"><strong>Anomalies:</strong> ${shipment.anomalies}</p>
                </div>
              `;

              new mapboxgl.Marker(originEl)
                .setLngLat(origin)
                .setPopup(new mapboxgl.Popup().setHTML(popupContent))
                .addTo(map.current);

              bounds.extend(origin);
            } catch (error) {
              console.warn(`MapPanel: Error adding origin marker for ${shipment.id}:`, error);
            }
          }

          // Add destination marker
          if (shipment.route && shipment.route.length > 1) {
            try {
              const destination = shipment.route[shipment.route.length - 1];
              const destEl = document.createElement('div');
              destEl.className = 'marker destination-marker';
              destEl.style.width = '16px';
              destEl.style.height = '16px';
              destEl.style.borderRadius = '50%';
              destEl.style.backgroundColor = color;
              destEl.style.border = '2px solid white';
              destEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
              destEl.style.cursor = 'pointer';

              const popupContent = `
                <div style="min-width: 200px;">
                  <h3 style="margin: 0 0 8px 0; color: ${color};">${shipment.id}</h3>
                  <p style="margin: 0 0 8px 0; font-size: 14px;"><strong>Origin:</strong> ${shipment.origin}</p>
                  <p style="margin: 0 0 8px 0; font-size: 14px;"><strong>Destination:</strong> ${shipment.destination}</p>
                  <p style="margin: 0 0 8px 0; font-size: 12px;"><strong>Status:</strong> ${shipment.status}</p>
                  <p style="margin: 0 0 8px 0; font-size: 12px;"><strong>Distance:</strong> ${shipment.distance} km</p>
                  <p style="margin: 0 0 8px 0; font-size: 12px;"><strong>Duration:</strong> ${shipment.duration} hrs</p>
                  <p style="margin: 0; font-size: 12px;"><strong>Anomalies:</strong> ${shipment.anomalies}</p>
                </div>
              `;

              new mapboxgl.Marker(destEl)
                .setLngLat(destination)
                .setPopup(new mapboxgl.Popup().setHTML(popupContent))
                .addTo(map.current);

              bounds.extend(destination);
            } catch (error) {
              console.warn(`MapPanel: Error adding destination marker for ${shipment.id}:`, error);
            }
          }

          // Extend bounds with route coordinates
          if (shipment.route) {
            shipment.route.forEach(coord => bounds.extend(coord));
          }
        });

        // Add all anomaly markers
        allAnomalyList.forEach((anomaly) => {
          if (anomaly.location) {
            try {
              const anomalyEl = document.createElement('div');
              anomalyEl.className = 'marker anomaly-marker';
              anomalyEl.style.width = '12px';
              anomalyEl.style.height = '12px';
              anomalyEl.style.borderRadius = '50%';
              anomalyEl.style.backgroundColor = anomaly.severity === 'critical' ? '#ef4444' : '#f59e0b';
              anomalyEl.style.border = '2px solid white';
              anomalyEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
              anomalyEl.style.cursor = 'pointer';

              const popupContent = `
                <div style="min-width: 200px;">
                  <h3 style="margin: 0 0 8px 0; color: ${anomaly.severity === 'critical' ? '#ef4444' : '#f59e0b'};">
                    ${anomaly.type.replace('_', ' ').toUpperCase()}
                  </h3>
                  <p style="margin: 0 0 8px 0; font-size: 14px;">${anomaly.description}</p>
                  <p style="margin: 0; font-size: 12px; color: #666;">
                    ${new Date(anomaly.timestamp).toLocaleString()}
                  </p>
                  <div style="margin-top: 8px; font-size: 12px;">
                    <strong>Sensor Data:</strong><br/>
                    Temp: ${anomaly.sensorData.temperature}°C<br/>
                    Humidity: ${anomaly.sensorData.humidity}%<br/>
                    Light: ${anomaly.sensorData.light} lux<br/>
                    Motion: ${anomaly.sensorData.motion}<br/>
                    Gas: ${anomaly.sensorData.gas}
                  </div>
                </div>
              `;

              new mapboxgl.Marker(anomalyEl)
                .setLngLat(anomaly.location)
                .setPopup(new mapboxgl.Popup().setHTML(popupContent))
                .addTo(map.current);

              bounds.extend(anomaly.location);
            } catch (error) {
              console.warn('MapPanel: Error adding anomaly marker:', error);
            }
          }
        });

        // Fit map to show all markers
        try {
          map.current.fitBounds(bounds, { padding: 50 });
        } catch (error) {
          console.warn('MapPanel: Error fitting bounds:', error);
        }

        console.log('MapPanel: All shipments added to map successfully');
      };

      // Try to initialize map
      if (!initMap()) {
        console.error('MapPanel: Failed to initialize map');
        setMapError(true);
        setMapLoading(false);
      }
    }, 1000); // 1 second delay

    return () => {
      clearTimeout(timer);
      removeMap();
    };
  }, [shipment, shipments, anomalies, allAnomalies, overviewMode]);

  return (
    <div className="flex-1 relative">
      {/* Map Container - Always rendered */}
      <div 
        ref={mapContainer}
        className="w-full h-full" 
        style={{ 
          minHeight: '400px', 
          height: '100%',
          backgroundColor: '#f0f0f0',
          position: 'relative'
        }}
      />
      
      {/* Loading Overlay */}
      {mapLoading && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">🗺️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Map...</h3>
            <p className="text-gray-600">Initializing MapBox...</p>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {mapError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">🗺️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Map Not Available</h3>
            <p className="text-gray-600 mb-4">Please check your MapBox API key configuration.</p>
            <div className="bg-white rounded-lg p-4 border border-gray-200 text-left text-sm">
              <p className="font-medium mb-2">Debugging steps:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-600">
                <li>Check browser console for error messages</li>
                <li>Verify your MapBox token is valid</li>
                <li>Ensure the token has proper permissions</li>
              </ol>
            </div>
          </div>
        </div>
      )}
      
      {/* Map Legend */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Legend</h4>
        <div className="space-y-2 text-xs">
          {overviewMode ? (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>SHIP-001</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>SHIP-002</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span>SHIP-003</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>SHIP-004</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span>SHIP-005</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span>Warning</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span>Critical</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>Origin</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>Destination</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>Route</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span>Warning</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>Critical</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapPanel; 